import React, { useState, useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Lato:wght@300;400;700;900&display=swap');

  :root {
    --russian-red:  #CD0302;
    --sunburst:     #F0C400;
    --edam:         #F5D19D;
    --citrusy:      #F88702;
    --green:        #4F7E17;
    --green-light:  #6aad1f;
    --edam-light:   #fdf3e3;
    --edam-dark:    #e8c070;
    --soil:         #3d2b1f;
    --cream:        #fffdf5;
    --fog:          #f7f2ea;
  }

  .faq-root {
    font-family: 'Lato', sans-serif;
    background:
      radial-gradient(ellipse at 10% 0%, rgba(79,126,23,0.09) 0%, transparent 55%),
      radial-gradient(ellipse at 90% 100%, rgba(240,196,0,0.08) 0%, transparent 55%),
      var(--cream);
    min-height: 100vh;
    padding: 40px 16px 64px;
    color: var(--soil);
  }

  .faq-inner {
    max-width: 820px;
    margin: 0 auto;
  }

  /* ── Header ── */
  .faq-header {
    text-align: center;
    margin-bottom: 48px;
  }

  .faq-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: var(--green);
    color: #fff;
    font-size: 0.68rem;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    padding: 5px 14px;
    border-radius: 20px;
    margin-bottom: 18px;
  }

  .faq-title {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: clamp(2rem, 5vw, 3.2rem);
    font-weight: 700;
    font-style: italic;
    color: var(--soil);
    line-height: 1.15;
    margin-bottom: 14px;
  }

  .faq-title span {
    color: var(--green);
  }

  .faq-divider {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin: 18px auto 22px;
  }

  .faq-divider-line {
    width: 60px;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--edam-dark));
    border-radius: 2px;
  }

  .faq-divider-line.right {
    background: linear-gradient(90deg, var(--edam-dark), transparent);
  }

  .faq-divider-dot {
    width: 10px;
    height: 10px;
    background: var(--citrusy);
    border-radius: 50%;
    box-shadow: 0 0 0 3px rgba(248,135,2,0.2);
  }

  .faq-subtitle {
    font-size: 0.97rem;
    color: #7a6a58;
    line-height: 1.7;
    max-width: 580px;
    margin: 0 auto;
  }

  /* ── Loading ── */
  .faq-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 0;
    gap: 18px;
  }

  .faq-spinner-wrap {
    position: relative;
    width: 56px;
    height: 56px;
  }

  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes pulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.5; transform:scale(0.85); } }

  .faq-spinner {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 3px solid rgba(79,126,23,0.15);
    border-top-color: var(--green);
    animation: spin 0.8s linear infinite;
  }

  .faq-spinner-inner {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 24px; height: 24px;
    background: var(--sunburst);
    border-radius: 50%;
    opacity: 0.7;
    animation: pulse 1.2s ease-in-out infinite;
  }

  .faq-loading-text {
    font-size: 0.92rem;
    font-weight: 700;
    color: var(--green);
    letter-spacing: 0.06em;
    animation: pulse 1.2s ease-in-out infinite;
  }

  /* ── Error State ── */
  .faq-error {
    background: rgba(205,3,2,0.05);
    border-left: 4px solid var(--russian-red);
    border-radius: 0 12px 12px 0;
    padding: 20px 22px;
    margin-bottom: 28px;
    display: flex;
    gap: 14px;
    align-items: flex-start;
  }

  .faq-error-icon { color: var(--russian-red); flex-shrink: 0; margin-top: 2px; }

  .faq-error-title {
    font-weight: 900;
    color: var(--russian-red);
    font-size: 0.88rem;
    margin-bottom: 4px;
  }

  .faq-error-msg { font-size: 0.88rem; color: #7a3030; }

  .btn-retry {
    display: inline-block;
    margin-top: 10px;
    padding: 7px 18px;
    background: linear-gradient(135deg, var(--russian-red), #a80201);
    color: #fff;
    border: none;
    border-radius: 20px;
    font-size: 0.82rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
    box-shadow: 0 3px 12px rgba(205,3,2,0.3);
  }

  .btn-retry:hover {
    transform: translateY(-1px);
    box-shadow: 0 5px 18px rgba(205,3,2,0.4);
  }

  /* ── FAQ Item ── */
  .faq-list { display: flex; flex-direction: column; gap: 14px; }

  .faq-item {
    background: #fff;
    border: 1.5px solid var(--edam);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(79,126,23,0.06);
    transition: box-shadow 0.25s, transform 0.2s, border-color 0.2s;
  }

  .faq-item:hover {
    box-shadow: 0 6px 24px rgba(79,126,23,0.13);
    border-color: var(--edam-dark);
    transform: translateY(-1px);
  }

  .faq-item.open {
    border-color: var(--green);
    box-shadow: 0 4px 20px rgba(79,126,23,0.15);
  }

  .faq-question {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 18px 20px;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: background 0.2s;
  }

  .faq-item.open .faq-question {
    background: linear-gradient(90deg, rgba(79,126,23,0.05), rgba(240,196,0,0.05));
    border-bottom: 1px dashed var(--edam-dark);
  }

  .faq-q-left { display: flex; align-items: flex-start; gap: 14px; flex: 1; }

  .faq-number {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--fog);
    border: 1.5px solid var(--edam-dark);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 0.82rem;
    font-weight: 700;
    color: var(--soil);
    transition: background 0.2s, border-color 0.2s, color 0.2s;
    margin-top: 1px;
  }

  .faq-item.open .faq-number {
    background: var(--green);
    border-color: var(--green);
    color: #fff;
  }

  .faq-q-text {
    font-size: 0.97rem;
    font-weight: 700;
    color: var(--soil);
    line-height: 1.45;
    transition: color 0.2s;
  }

  .faq-item.open .faq-q-text,
  .faq-item:hover .faq-q-text {
    color: var(--green);
  }

  .faq-chevron {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--fog);
    border: 1.5px solid var(--edam-dark);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.25s, transform 0.3s, border-color 0.2s;
    color: #888;
  }

  .faq-item.open .faq-chevron {
    background: var(--green);
    border-color: var(--green);
    color: #fff;
    transform: rotate(180deg);
  }

  /* ── Answer ── */
  .faq-answer-wrap {
    overflow: hidden;
    transition: max-height 0.45s cubic-bezier(0.4,0,0.2,1), opacity 0.35s;
  }

  .faq-answer {
    font-size: 0.93rem;
    color: #5a4a3a;
    line-height: 1.75;
    border-left: 3px solid var(--edam-dark);
    margin: 0 20px 18px 20px;
    background: rgba(245,209,157,0.08);
    border-radius: 0 8px 8px 0;
    padding: 14px 18px;
  }

  /* ── Empty State ── */
  .faq-empty {
    background: #fff;
    border: 2px dashed var(--edam-dark);
    border-radius: 20px;
    padding: 56px 24px;
    text-align: center;
  }

  .faq-empty-icon { color: var(--edam-dark); margin: 0 auto 16px; display: block; }

  .faq-empty-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--soil);
    margin-bottom: 8px;
  }

  .faq-empty-sub { font-size: 0.9rem; color: #888; margin-bottom: 22px; }

  .btn-refresh {
    padding: 10px 26px;
    background: linear-gradient(135deg, var(--green), var(--green-light));
    color: #fff;
    border: none;
    border-radius: 22px;
    font-weight: 700;
    font-size: 0.88rem;
    letter-spacing: 0.05em;
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
    box-shadow: 0 4px 14px rgba(79,126,23,0.3);
  }

  .btn-refresh:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(79,126,23,0.4);
  }

  /* ── Footer Note ── */
  .faq-footer-note {
    margin-top: 48px;
    background: linear-gradient(135deg, rgba(79,126,23,0.07) 0%, rgba(240,196,0,0.07) 100%);
    border: 1.5px solid var(--edam-dark);
    border-radius: 18px;
    padding: 28px 30px;
    display: flex;
    gap: 18px;
    align-items: flex-start;
    position: relative;
    overflow: hidden;
  }

  .faq-footer-note::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 5px;
    background: linear-gradient(180deg, var(--green), var(--citrusy));
    border-radius: 18px 0 0 18px;
  }

  .faq-footer-icon-wrap {
    flex-shrink: 0;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: var(--green);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 1.2rem;
    box-shadow: 0 3px 12px rgba(79,126,23,0.3);
  }

  .faq-footer-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--soil);
    margin-bottom: 6px;
  }

  .faq-footer-body { font-size: 0.9rem; color: #6a5a48; line-height: 1.65; }

  /* ── Decorative leaf bg ── */
  .faq-deco {
    position: fixed;
    pointer-events: none;
    opacity: 0.04;
    z-index: 0;
  }
`;

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    AOS.init({ duration: 800, once: true, easing: 'ease-out' });
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/api/faqs');
      if (!response.ok) throw new Error('Failed to fetch FAQs');
      const data = await response.json();
      if (data.success && data.faqs && Array.isArray(data.faqs) && data.faqs.length > 0) {
        setFaqs(data.faqs);
      } else {
        setFaqs([]);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError('FAQ তথ্য লোড করতে সমস্যা হয়েছে। পরে আবার চেষ্টা করুন।');
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <style>{styles}</style>
      <div className="faq-root">

        <div className="faq-inner">

          {/* ── Header ── */}
          <div className="faq-header" data-aos="fade-down">
            <div className="faq-eyebrow">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
              Help Center
            </div>

            <h1 className="faq-title">
              Frequently Asked <span>Questions</span>
            </h1>

            <div className="faq-divider">
              <div className="faq-divider-line"></div>
              <div className="faq-divider-dot"></div>
              <div className="faq-divider-line right"></div>
            </div>

            <p className="faq-subtitle">
              Welcome to our FAQ section! Here you will find answers to common questions about our products, ordering process, shipping, returns and other important topics.
            </p>
          </div>

          {/* ── Loading ── */}
          {loading && (
            <div className="faq-loading">
              <div className="faq-spinner-wrap">
                <div className="faq-spinner" />
                <div className="faq-spinner-inner" />
              </div>
              <p className="faq-loading-text">Loading FAQs...</p>
            </div>
          )}

          {/* ── Error ── */}
          {error && !loading && (
            <div className="faq-error" data-aos="fade-up">
              <svg className="faq-error-icon" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="faq-error-title">Something went wrong</p>
                <p className="faq-error-msg">{error}</p>
                <button className="btn-retry" onClick={fetchFAQs}>Try Again</button>
              </div>
            </div>
          )}

          {/* ── FAQ List ── */}
          {!loading && !error && faqs.length > 0 && (
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <div
                  key={faq.id || index}
                  className={`faq-item ${openIndex === index ? 'open' : ''}`}
                >
                  <button
                    className="faq-question"
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={index === openIndex}
                  >
                    <div className="faq-q-left">
                      <span className="faq-number">{index + 1}</span>
                      <h3 className="faq-q-text">{faq.question}</h3>
                    </div>
                    <div className="faq-chevron">
                      <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  <div
                    className="faq-answer-wrap"
                    style={{
                      maxHeight: openIndex === index ? '1000px' : '0px',
                      opacity: openIndex === index ? 1 : 0,
                    }}
                  >
                    <div className="faq-answer">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Empty State ── */}
          {!loading && !error && faqs.length === 0 && (
            <div className="faq-empty" data-aos="fade-up">
              <svg className="faq-empty-icon" width="72" height="72" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="faq-empty-title">No FAQs Available</p>
              <p className="faq-empty-sub">এই মুহূর্তে কোনো FAQ পাওয়া যায়নি। পরে আবার চেষ্টা করুন।</p>
              <button className="btn-refresh" onClick={fetchFAQs}>↻ Refresh</button>
            </div>
          )}

          {/* ── Footer Note ── */}
          {!loading && faqs.length > 0 && (
            <div
              className="faq-footer-note"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="faq-footer-icon-wrap">
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <p className="faq-footer-title">Still have questions?</p>
                <p className="faq-footer-body">
                  Our customer service team is here to help. Contact us through the information provided on our Contact page or reach out via email.
                </p>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default FAQPage;
